/**
 * Accorde un nom en nombre. **Zéro est singulier** (règle française).
 *
 * Le pluriel par défaut est le singulier suffixé d'un `s` — ce qui couvre
 * « jour », « élève », « exercice », « appel », « observation ». Les pluriels
 * irréguliers (« travail » → « travaux ») se passent en troisième argument.
 *
 * @example pluriel(0, 'élève')  // 'élève'
 * @example pluriel(1, 'élève')  // 'élève'
 * @example pluriel(2, 'élève')  // 'élèves'
 */
export function pluriel(n, singulier, plurielExplicite) {
    return Math.abs(n) < 2 ? singulier : (plurielExplicite ?? `${singulier}s`);
}
/**
 * Le nombre ET son nom accordé : « 0 élève », « 1 séance », « 4 jours ».
 *
 * C'est la forme qu'il faut appeler pour composer une phrase — `pluriel` seul
 * laisse au point d'appel la liberté d'oublier le nombre, et donc de le
 * réintroduire sans accord.
 */
export function denombrer(n, singulier, plurielExplicite) {
    return `${n} ${pluriel(n, singulier, plurielExplicite)}`;
}
/**
 * Accorde un participe ou un adjectif qui SUIT le nom dénombré :
 * « 0 élève suivi », « 3 élèves suivis ». Même règle de nombre que `pluriel`,
 * exposée à part parce qu'un appel qui accorde le nom oublie régulièrement de
 * poursuivre l'accord — c'est littéralement le défaut de `MOB-TUT-JOU-10`,
 * où « suivis » restait au pluriel sous « 0 élève ».
 */
export function accordNombre(n, singulier, plurielExplicite) {
    return pluriel(n, singulier, plurielExplicite);
}
/** Les mots devant lesquels « de », « le », « la », « ce » s'élident. */
const VOYELLES = 'aàâeéèêëiîïoôuùûüyAÀÂEÉÈÊËIÎÏOÔUÙÛÜY';
/**
 * `true` si le mot commence par une voyelle ou un `h` muet, et appelle donc
 * l'élision. La liste des `h` muets est celle des mots que le produit écrit —
 * elle est courte exprès : un dictionnaire complet ici serait du décor.
 */
function appelleElision(mot) {
    const m = mot.trimStart();
    const premiere = m.charAt(0);
    if (VOYELLES.includes(premiere))
        return true;
    if (premiere !== 'h' && premiere !== 'H')
        return false;
    return /^h(eure|istoire|abitude|ypoth|orizon|umain|onnêt)/i.test(m);
}
/**
 * Élide un déterminant ou une préposition devant le mot qui suit.
 *
 * @example elide('de', 'exercices')  // "d'exercices"
 * @example elide('de', 'séances')    // 'de séances'
 * @example elide('le', 'objectif')   // "l'objectif"
 */
export function elide(prefixe, mot) {
    if (!appelleElision(mot)) {
        // « ce » a une forme propre devant voyelle : « cet exercice », pas « c'exercice ».
        return `${prefixe} ${mot}`;
    }
    if (prefixe === 'ce')
        return `cet ${mot}`;
    return `${prefixe.slice(0, -1)}’${mot}`;
}
/**
 * Accorde une tournure sur le genre déclaré de l'élève.
 *
 * ⚠️ À n'employer que là où aucune reformulation épicène n'existe. Partout
 * ailleurs, `formuleAvecPrenom` est la bonne réponse : elle se lit pour les
 * quatre valeurs de `Genre` sans qu'aucune ne soit un cas particulier.
 */
export function accordGenre(genre, v) {
    if (genre === 'fille')
        return v.feminin;
    if (genre === 'garcon')
        return v.masculin;
    return v.neutre;
}
/**
 * Compose une phrase d'interface en y posant le prénom de l'élève, ou un
 * repli épicène quand le prénom n'est pas disponible dans le contexte.
 *
 * Le gabarit porte `{prenom}` — même convention que les fiches de repères
 * (`apps/mobile/src/features/parent/reperes.contenu.ts`), pour qu'un rédacteur
 * n'ait qu'un seul marqueur à connaître.
 *
 * @example formuleAvecPrenom('Où en est {prenom}', 'Léa')  // 'Où en est Léa'
 * @example formuleAvecPrenom('Où en est {prenom}', null)   // 'Où en est votre enfant'
 */
export function formuleAvecPrenom(gabarit, prenom, repli = 'votre enfant') {
    return gabarit.replaceAll('{prenom}', prenom?.trim() || repli);
}
/**
 * Retire le point final d'un texte que l'interface va coudre à une seconde
 * proposition.
 *
 * ─── Ce que ça corrige (MOB-PAR-P2-05) ─────────────────────────────────────
 *
 * La fiche enfant rendait « Consolider : Calculer la circonférence et l'aire
 * d'un cercle**.**, d'ici le 29 septembre. » — le serveur possède la
 * description de l'objectif ET sa ponctuation, le client y ajoutait une
 * incise. Le bon geste n'est pas de réécrire le texte du serveur (ce serait
 * recréer une règle éditoriale côté client), c'est de retirer la ponctuation
 * de FIN avant la couture, et rien d'autre.
 *
 * Seul le point est retiré : « ? » et « ! » portent un sens qu'on ne peut pas
 * effacer, et une phrase d'objectif n'en porte jamais.
 */
export function sansPointFinal(texte) {
    return texte.trimEnd().replace(/\.$/, '');
}
//# sourceMappingURL=accord.js.map